import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import UploadedImageThumb from "../../components/UploadedImageThumb";
import aspectRatios, { AspectRatioIcon } from "../../constants/aspectRatios";
import resolutionMap, { RESOLUTION_OPTIONS } from "../../constants/resolutionMap";
import AngleViewDropdown from '../../components/AngleViewDropdown';
import RoomTypeDropdown from '../../components/RoomTypeDropdown';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import { generateMoodboardTo3D } from "../../services/geminiService";
import { generateWithFlux, isFluxModel } from "../../services/fluxService";
import { useAuth } from "../../contexts/AuthContext";
import { useProjects } from "../../contexts/ProjectContext";
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from "../../services/supabaseService";
import {
  subscribeToDesignUpdates,
  subscribeToGenerationUpdates,
  subscribeToProfileUpdates,
} from "../../services/realtimeService";
import { supabase } from "../../lib/supabase";
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';

const MoodboardTo3DRoom = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);

  const scrollGuideTarget = (step) => {
    const desktopRefs = [moodboardUploadDesktopRef, roomLayoutDesktopRef, applyToggleDesktopRef, roomSizeDesktopRef, assetLibraryDesktopRef, roomTypeDesktopRef, angleViewDesktopRef, modelDesktopRef, aspectRatioDesktopRef, resolutionGuideDesktopRef, generateBtnDesktopRef, resultDesktopRef];
    const mobileRefs = [moodboardUploadMobileRef, roomLayoutMobileRef, applyToggleMobileRef, roomSizeMobileRef, assetLibraryMobileRef, roomTypeMobileRef, angleViewMobileRef, modelMobileRef, aspectRatioMobileRef, resolutionGuideMobileRef, generateBtnMobileRef, resultMobileRef];
    const ref = window.innerWidth >= 768 ? desktopRefs[step - 1]?.current : mobileRefs[step - 1]?.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const openGuide = () => {
    setGuideStep(1);
    setShowGuide(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setGuideVisible(true));
    });
  };

  const nextGuideStep = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = nextStep === 4 || nextStep === 10 || nextStep === 11 || nextStep === 12;
    if (needsScroll) {
      scrollGuideTarget(nextStep);
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        // Extra delay to let scroll finish before showing bubble
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 400);
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setGuideVisible(true));
        });
      }
    }, 600);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
    }, 600);
  };
  const moodboardUploadDesktopRef = useRef(null);
  const moodboardUploadMobileRef = useRef(null);
  const roomLayoutDesktopRef = useRef(null);
  const roomLayoutMobileRef = useRef(null);
  const applyToggleDesktopRef = useRef(null);
  const applyToggleMobileRef = useRef(null);
  const roomSizeDesktopRef = useRef(null);
  const roomSizeMobileRef = useRef(null);
  const assetLibraryDesktopRef = useRef(null);
  const assetLibraryMobileRef = useRef(null);
  const roomTypeDesktopRef = useRef(null);
  const roomTypeMobileRef = useRef(null);
  const angleViewDesktopRef = useRef(null);
  const angleViewMobileRef = useRef(null);
  const modelDesktopRef = useRef(null);
  const modelMobileRef = useRef(null);
  const aspectRatioDesktopRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionGuideDesktopRef = useRef(null);
  const resolutionGuideMobileRef = useRef(null);
  const generateBtnDesktopRef = useRef(null);
  const generateBtnMobileRef = useRef(null);
  const resultDesktopRef = useRef(null);
  const resultMobileRef = useRef(null);
  const [moodboardImages, setMoodboardImages] = useState([]);
  const [referenceImages, setReferenceImages] = useState([]);
  const [selectedModel, setSelectedModel] = useState("Gemini 3.1 Flash Image Preview");
  const [isModelOpen, setIsModelOpen] = useState(false); // kept for compatibility
  const [selectedAspectRatio, setSelectedAspectRatio] = useState("Auto");
  const [selectedRoomType, setSelectedRoomType] = useState("--");
  const [selectedAngleShot, setSelectedAngleShot] = useState("--");
  const [promptText, setPromptText] = useState("");
  const [additionalPromptText, setAdditionalPromptText] = useState("");
  const [isRefining, setIsRefining] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [refinedPrompt, setRefinedPrompt] = useState("");
  const [refineError, setRefineError] = useState("");
  const [generateError, setGenerateError] = useState("");
  const textareaRef = useRef(null);
  const additionalTextareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [realtimeDesigns, setRealtimeDesigns] = useState([]);
  const [generatedDesigns, setGeneratedDesigns] = useState([]);
  const [draggedImages, setDraggedImages] = useState([]);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [selectedRoomSize, setSelectedRoomSize] = useState(null);
  const [applyRoomLayout, setApplyRoomLayout] = useState(false);

  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();

  const roomSizes = [
    { id: "small", label: "Small", size: "≤ 12 m²" },
    { id: "medium", label: "Medium", size: "13 – 25 m²" },
    { id: "large", label: "Large", size: "26 – 40 m²" },
    { id: "extra-large", label: "Extra-Large", size: "40+ m²" },
  ];

  // Add this block - Define missing state variables for button handlers
  const [buttonLoading, setButtonLoading] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  // Add this block - Define missing state variables for aspect ratio dropdown
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState("1K");
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const aspectRatioRef = useRef(null);
  const resolutionRef = useRef(null);
  const resolutionMobileRef = useRef(null);

  // Models constant removed - using shared ModelSelectionDropdown component

  // Real-time subscription for design updates when project is active
  useEffect(() => {
    if (!currentProjectId || !user) return;

    const unsubscribe = subscribeToDesignUpdates(currentProjectId, {
      onInsert: (newDesign) => {
        // Add new design to realtime list
        setRealtimeDesigns((prev) => [newDesign, ...prev]);

        // Update generated images if it's a new image
        if (
          newDesign?.imageUrl &&
          !generatedImages?.includes(newDesign?.imageUrl)
        ) {
          setGeneratedImages((prev) => [
            newDesign?.imageUrl,
            ...prev?.slice(0, 3),
          ]);
        }
      },
      onUpdate: (updatedDesign) => {
        // Update design in realtime list
        setRealtimeDesigns((prev) =>
          prev?.map((d) => (d?.id === updatedDesign?.id ? updatedDesign : d)),
        );
      },
      onDelete: (deletedDesign) => {
        // Remove design from realtime list
        setRealtimeDesigns((prev) =>
          prev?.filter((d) => d?.id !== deletedDesign?.id),
        );
      },
    });

    return () => {
      unsubscribe();
    };
  }, [currentProjectId, user]);

  // Real-time subscription for generation metadata updates
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToGenerationUpdates(user?.id, {
      onInsert: (newMetadata) => {
        console.log("New generation activity detected:", newMetadata);
        // Could show notification or update UI
      },
      onUpdate: (updatedMetadata) => {
        console.log("Generation metadata updated:", updatedMetadata);
      },
    });

    return () => {
      unsubscribe();
    };
  }, [user]);

  // Real-time subscription for user profile updates (credit changes)
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToProfileUpdates(
      user?.id,
      (updatedProfile) => {
        console.log("User profile updated in real-time:", updatedProfile);
        // Profile updates are handled by AuthContext, but we can log them here
      },
    );

    return () => {
      unsubscribe();
    };
  }, [user]);

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const urls = designs.map((d) => d.imageUrl).filter(Boolean);
        setGeneratedImages(urls);
        setGeneratedDesigns(designs);
        setCurrentImage(urls[0]);
        // Restore generation settings from metadata
        setCurrentDesignId(designs[0].id);
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedAspectRatio(metadata.imageSize);
          if (metadata.roomType && metadata.roomType !== 'Room') setSelectedRoomType(metadata.roomType);
          if (metadata.angleView && metadata.angleView !== '--' && metadata.angleView !== 'Top View') setSelectedAngleShot(metadata.angleView);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          if (metadata.additionalParams?.promptText) setPromptText(metadata.additionalParams.promptText);
          else if (designs[0].prompt) setPromptText(designs[0].prompt);
          if (metadata.additionalParams?.additionalPromptText) setAdditionalPromptText(metadata.additionalParams.additionalPromptText);
          if (metadata.additionalParams?.selectedResolution) setSelectedResolution(metadata.additionalParams.selectedResolution);
          if (metadata.additionalParams?.selectedRoomSize) setSelectedRoomSize(metadata.additionalParams.selectedRoomSize);
        }
      }
    };
    loadProjectData();
  }, []);

  // Get image dimensions based on selected aspect ratio and resolution
  const getImageDimensions = () => {
    const ratioResolutions = resolutionMap[selectedAspectRatio];
    if (ratioResolutions && ratioResolutions[selectedResolution]) {
      return ratioResolutions[selectedResolution];
    }
    return { width: 1024, height: 1024 };
  };

  // Get dimensions for a specific resolution key (used by dropdown display)
  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedAspectRatio];
    if (ratioMap && ratioMap[resKey]) {
      return ratioMap[resKey];
    }
    return resolutionMap['1:1'][resKey] || { width: 1024, height: 1024 };
  };

  const MAX_TOTAL_UPLOADS = 6;
  const totalUploads = moodboardImages.length + referenceImages.length;
  const isUploadLimitReached = totalUploads >= MAX_TOTAL_UPLOADS;

  const handleMoodboardUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = moodboardImages.length + referenceImages.length;
    const remaining = Math.max(0, MAX_TOTAL_UPLOADS - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const imageUrls = await Promise.all(
      limitedFiles.map((file) => storageService.uploadFile(file, user?.id))
    );
    setMoodboardImages([...moodboardImages, ...imageUrls.filter(Boolean)]);
  };

  const handleReferenceUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    const totalCurrent = moodboardImages.length + referenceImages.length;
    const remaining = Math.max(0, MAX_TOTAL_UPLOADS - totalCurrent);
    if (remaining === 0) return;
    const limitedFiles = files.slice(0, remaining);
    const imageUrls = await Promise.all(
      limitedFiles.map((file) => storageService.uploadFile(file, user?.id))
    );
    setReferenceImages([...referenceImages, ...imageUrls.filter(Boolean)]);
  };

  const handleRemoveMoodboard = (idx) => {
    setMoodboardImages(moodboardImages.filter((_, i) => i !== idx));
  };

  const handleRemoveReference = (idx) => {
    setReferenceImages(referenceImages.filter((_, i) => i !== idx));
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    setRefineError("");
    setGenerateError("");

    // Validate required inputs: moodboard and reference layout
    if (moodboardImages?.length === 0) {
      setRefineError("Please upload at least one moodboard image.");
      isGeneratingRef.current = false;
      return;
    }


    // FIXED: Check authentication using Supabase session directly (source of truth)
    // This prevents auth state mismatch between page load and generate action
    console.log("[AUTH DEBUG] Generate Image clicked");
    console.log("[AUTH DEBUG] AuthContext state:", {
      user: user?.id,
      loading,
      profileLoading,
    });

    // Get current session directly from Supabase (bypasses React state)
    const {
      data: { session },
      error: sessionError,
    } = await supabase?.auth?.getSession();

    console.log("[AUTH DEBUG] Supabase session:", {
      sessionExists: !!session,
      userId: session?.user?.id,
      sessionError,
    });

    // Wait for loading states to complete
    if (loading || profileLoading) {
      setGenerateError("Loading user session, please wait...");
      isGeneratingRef.current = false;
      return;
    }

    // Check session from Supabase (source of truth) instead of AuthContext state
    if (!session || !session?.user?.id) {
      console.error("[AUTH ERROR] No valid session found");
      setGenerateError("Please sign in to generate images.");
      isGeneratingRef.current = false;
      return;
    }

    // Use session user for all subsequent operations
    const currentUser = session?.user;
    console.log("[AUTH DEBUG] Using session user:", currentUser?.id);

    // Credit check (supports team members sharing owner's pool)
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, getModelCreditCost(selectedModel));
    if (!creditCheck?.allowed) {
      setGenerateError(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        setGenerateError(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    setIsGenerating(true);
    startTimer();
    setCurrentImage(null);

    try {
      // Generate image directly with Gemini using multimodal request
      // NO Claude refinement - send user text exactly as written
      abortControllerRef.current = new AbortController();

      const dimensions = getImageDimensions();

      // Build material images object
      const materialImages = {};
      // Add material images if you have state for them (wallsMaterial, floorMaterial, etc.)
      // For now, leaving empty as the UI doesn't show material upload fields

      // Combine all moodboard images (uploaded via + button, dragged, and from moodboard state)
      const allMoodboardImages = [
        ...moodboardImages,
        ...draggedImages,
      ];

      // Determine active mode
      const hasReference = referenceImages?.length > 0;
      const activeMode = !hasReference ? 'B' : (applyRoomLayout ? 'C' : 'A');

      const moodboardSystemPrompt = `Role: You are a neutral translator between interior design platform user inputs and Gemini 3.1 Pro image generation model for moodboard-to-room visualization. Apply professional interior design composition logic — spatial harmony, material coordination, proportional furniture placement, functional zoning, and visual weight distribution — with the seasoned judgment of a 10-year professional interior designer. Your task is to produce an interior masterpiece from the user's reference images while strictly using only the references provided by the user. Do not invent, add, or creatively expand beyond what is literally present in the uploaded images — unless operating in Mode B where inventory completion is explicitly permitted.
Send all image references and text inputs uploaded by the user directly to Gemini API in one single call.

Intro: This tool transforms 2D moodboard collages — collections of interior pieces (furniture, textiles, materials, objects etc.) placed together in one image — into hyper-realistic 3D room visualizations. The tool operates in three modes determined by which inputs the user provides. In all modes, the moodboard defines the design DNA: the furniture pieces, materials, textures, colour palette, and atmosphere. The goal is to produce a professionally composed, harmonious, and aesthetically compelling interior that honours the user's moodboard vision.

MODE DETECTION
Determine the active mode from the user's inputs:
MODE A — Moodboard + Room Reference (flexible replacement) Activates when: moodboardImages are uploaded AND referenceImages are uploaded AND applyRoomLayout is false (toggle OFF).
MODE B — Moodboard Only (AI-constructed room) Activates when: moodboardImages are uploaded AND referenceImages are NOT uploaded.
MODE C — Moodboard + Room Reference + Apply Layout (full integration) Activates when: moodboardImages are uploaded AND referenceImages are uploaded AND applyRoomLayout is true (toggle ON).

ACTIVE MODE: ${activeMode}${activeMode === 'A' ? ` (applyRoomLayout = false)` : activeMode === 'B' ? ` (no referenceImages uploaded)` : ` (applyRoomLayout = true)`}

${activeMode === 'A' ? `MODE A — FLEXIBLE REPLACEMENT
What this mode does: The user provides a 2D moodboard and a room layout reference. The AI uses the room reference as a dimensional and architectural guide — not as an immovable spatial cage. The AI replaces furniture, materials, and objects found in the room reference with their functional equivalents from the moodboard, then harmonizes the entire composition to produce a professionally designed, beautiful room.
Task:
Step 1 — Analyse the moodboard. Identify every distinct furniture piece, material sample, textile, decorative object, and surface finish visible in the moodboard collage. For each element, determine its primary function in the human world.
Step 2 — Analyse the room reference. Identify the room's architectural envelope: walls, ceiling, floor, windows, doors, built-in elements. Identify every furniture piece and material currently in the room. For each, classify by the same functional categories. Note the room's camera angle, perspective, approximate dimensions, ceiling height, and natural light direction.
Step 3 — Functional mapping and replacement. Match each functional element in the room reference to its moodboard counterpart. Bed replaces bed. Sofa replaces sofa. Floor material replaces floor material. Wall treatment replaces wall treatment. Lighting replaces lighting. Where the room contains a functional element that has no moodboard counterpart, retain it but adapt its finish and colour to harmonize with the moodboard palette. Where the moodboard contains an element that has no room counterpart, integrate it into the composition at a location where a professional interior designer would logically place it.
Step 4 — Harmonize and compose. You have creative permission to adjust the spatial arrangement for professional-quality results. The room reference provides real-world dimensional guidance, human-scale proportions, and architectural context — but you may refine furniture positions, adjust groupings, shift spatial relationships, and optimize circulation paths to achieve a harmonious, aesthetically elevated composition. The room reference is a guide, not a constraint. The final image must look like a deliberately designed interior, not a mechanical swap of objects in fixed positions.
Step 5 — Render. Generate a hyper-realistic 3D room that integrates all moodboard elements into the architectural context derived from the room reference. Ensure perspective accuracy, natural lighting, realistic shadows, material textures at photographic quality, and dimensional plausibility throughout.` : ''}

${activeMode === 'B' ? `MODE B — AI-CONSTRUCTED ROOM FROM MOODBOARD
What this mode does: The user provides only a 2D moodboard collage with no room reference. The AI must construct an entire room from scratch, transforming flat 2D schematic furniture pieces and material samples into a volumetric, spatially convincing, hyper-realistic 3D interior.
Task:
Step 1 — Analyse the moodboard inventory. Identify every distinct furniture piece, material sample, textile, decorative object, and surface finish. Classify each by function. Assess the overall colour palette, material language, and design character conveyed by the collection.
Step 2 — Assess completeness. Determine whether the moodboard provides enough elements to compose a complete, livable room. A complete room typically requires: primary seating or sleeping furniture, floor treatment, wall treatment, at least one lighting source, and supporting objects (side tables, storage, textiles). If the moodboard inventory is incomplete, identify the missing functional categories.
Step 3 — Inventory completion (Mode B only). For any missing functional category identified in Step 2, the AI must design and introduce complementary furniture, materials, or objects that share the DNA of the uploaded moodboard — matching its colour palette, material language, proportional style, and design character. These additions must feel like natural extensions of the moodboard collection, as if the same designer curated them. Every addition must serve a clear functional or compositional purpose. Do not add generic filler. Think as a 10-year professional interior designer completing a client's vision.
Step 4 — Dimensional construction. Using selectedRoomSize as the physical anchor:
${selectedRoomSize === 'small' ? "* 'small': up to 12 m² — compact, intimate proportions. Ceiling height approximately 2.5–2.7 m." : selectedRoomSize === 'medium' ? "* 'medium': 13–25 m² — standard residential room. Ceiling height approximately 2.7–3.0 m." : selectedRoomSize === 'large' ? "* 'large': 26–40 m² — spacious, open proportions. Ceiling height approximately 3.0–3.5 m." : selectedRoomSize === 'extra-large' ? "* 'extra-large': 40+ m² — expansive, grand proportions. Ceiling height 3.5 m+." : "No room size selected — use medium (13–25 m²) as default."}
Construct a room volume that corresponds to these real-world dimensions. Walls, floor area, ceiling height, and spatial depth must feel true to the stated size. Every furniture piece must be rendered at its correct human-world scale within this volume — a sofa is approximately 2.0–2.5 m wide, a coffee table is approximately 0.4–0.5 m tall, a dining chair seat is approximately 0.45 m high, a bed is approximately 2.0 m long. Dimensional plausibility is non-negotiable.
Step 5 — Spatial composition. Arrange all elements (moodboard inventory + any AI completions) using professional interior design logic: create a clear focal point, establish functional zones, ensure logical circulation paths, balance visual weight across the room, layer foreground/midground/background depth. The composition must feel like an intentional, curated interior — not objects scattered in a void.
Step 6 — Volume, shadow, and realism. This is critical: every object must be rendered with full 3D volume, realistic cast shadows, ambient occlusion, surface reflections appropriate to material type, and consistent lighting direction. The 2D moodboard items must be completely transformed from flat samples into tangible, physically present objects occupying real space. No floating objects, no flat pasted appearances, no inconsistent shadow directions.
Step 7 — Render. Generate a hyper-realistic 3D room at the selected dimensions, with all elements fully volumetric and spatially integrated.` : ''}

${activeMode === 'C' ? `MODE C — FULL INTEGRATION (STRONGEST COMBINATION)
What this mode does: The user provides a moodboard, a room reference, AND activates the "Apply Room Layout design onto final generation" toggle. This is the strongest combination — the AI uses the full architectural and spatial truth of the room reference as the foundation, then applies the moodboard's materials, textures, colour palette, and atmosphere onto it. The room reference's spatial DNA (perspective, camera angle, architectural proportions, spatial depth, light direction) is fully honoured. The moodboard's design DNA (colour, material, texture, furniture style, decorative character) is fully applied.
Task:
Step 1 — Analyse the room reference as the spatial foundation. Lock the 3D spatial box: wall positions, ceiling height, floor plane, window placements, door locations, built-in architectural elements, camera angle, vanishing points, perspective geometry, and natural light direction. These are the immovable architectural skeleton.
Step 2 — Analyse the moodboard as the design layer. Identify every furniture piece, material, textile, object, and surface finish. These define what the room looks like — colour, texture, material, and atmosphere — not where things go.
Step 3 — Layer the moodboard onto the spatial foundation. Apply moodboard materials to corresponding room surfaces: moodboard floor material onto the room's floor plane, moodboard wall treatment onto the room's walls, moodboard furniture pieces placed into the room's spatial layout at positions that respect the architectural logic. The room's spatial DNA (vanishing points, proportions, perspective, lighting direction, ceiling height ratio, architectural rhythm) transfers into the final image. The moodboard's design DNA (colour palette, material finishes, patterns, textures, object forms, furniture shapes) dresses the space.
Step 4 — Professional refinement. Within the locked spatial framework, apply professional composition logic: optimize furniture placement for functional zoning and visual balance, ensure material transitions are coherent (floor-to-wall, wall-to-ceiling), verify that the moodboard's colour palette harmonizes across all surfaces when applied together in 3D space. Where a moodboard element cannot be placed at an exact room reference position without compromising aesthetics, make minor positional adjustments to achieve a beautiful result — the spatial framework guides, but beauty is the goal.
Step 5 — Render. Generate a hyper-realistic 3D room where the room reference's architecture is fully recognizable and the moodboard's design language is fully applied. The result must feel like the same room, redesigned by a top interior designer using exactly the materials and furniture the user selected.` : ''}

HIGH-QUALITY KEYWORDS
Maintain in every generation across all modes: render the combination of moodboard and spatial context into a hyper-realistic interior, hyper realism, photorealism, 3Ds Max quality with textures and realistic surfaces, shot on professional camera, interior design magazine editorial quality, physically accurate lighting and shadows, volumetric depth, material texture fidelity.

TEMPERATURE
1 (default Gemini).

INPUT HIERARCHY
You will receive these inputs from the user and must send them all to Gemini API in one call, in the following importance hierarchy from one to ten:
1. moodboardImages (REQUIRED) — 2D collage of interior furniture, materials, textiles and objects selected by the user. Defines the design DNA: what furniture pieces, materials, textures, colours, and objects must appear in the rendered room. ${activeMode === 'B' ? 'These are the complete starting inventory from which the AI constructs the room (with permitted inventory completion for missing functional categories).' : 'Integrate each visible piece faithfully into the spatial layout.'}
2. referenceImages — the room layout reference. ${activeMode === 'A' ? 'Mode A (applyRoomLayout = false): Use as a dimensional and architectural guide. Provides real-world human proportions, room shape, and camera perspective — but the AI has creative freedom to adjust the spatial arrangement for harmonious results. Replace the room\'s existing furniture and materials with moodboard equivalents by function.' : activeMode === 'B' ? 'Mode B (no referenceImages uploaded): Not present. AI constructs room from scratch using selectedRoomSize for dimensional guidance.' : 'Mode C (applyRoomLayout = true): Use as the full spatial foundation. The room\'s architectural skeleton, perspective, camera angle, and spatial structure are honoured. The moodboard\'s design DNA is applied onto this structure.'}
3. promptText 1 — ${promptText || "None provided"}. If provided, apply each object or material instruction to the rendered 3D room per spatial composition logic. Do not substitute or ignore.
4. selectedRoomType — ${selectedRoomType !== "--" ? selectedRoomType : "--"}. Include as spatial context and functional confirmation only, if not '--'. This confirms the room's purpose so the variation respects functional logic — beds belong in bedrooms, dining tables serve dining areas, etc.
5. selectedAngleShot — ${selectedAngleShot !== "--" ? selectedAngleShot : "--"}. Apply the exact camera angle chosen by the user in the dropdown and reflect it in the final 3D rendered room.
6. additionalPromptText (promptText 2) — ${additionalPromptText || "None provided"}. Include the user's text literally, word for word.
7. selectedRoomSize — ${selectedRoomSize || "not selected"}. Defines the physical volume the generated room must visually represent. Especially critical in Mode B where no room reference provides dimensional context.
8. selectedAspectRatio — ${selectedAspectRatio}. Extract ratio strictly from what the user selected in the UI. If Auto, pass the aspect ratio of referenceImages (Mode A/C) or use the selected value. If a specific size is selected, pass that value exactly. Do not hardcode any dimensions.
9. selectedResolution — ${selectedResolution}. Pass the resolution value selected by the user. Do not override.
10. selectedModel — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.

STRICT RULES
* Do not invent materials, furniture, lighting, or atmosphere not present in the moodboard inputs — except in Mode B where inventory completion is explicitly permitted for missing functional categories, and even then, additions must match the moodboard's design DNA.
* Do not use any style labels, unless the user explicitly wrote them in promptText. Let the visual references define the style naturally through their forms, finishes, and colours.
${activeMode === 'A' ? '* In Mode A: the room reference is a guide for human-scale proportions and architectural context, not an immovable spatial constraint. The AI has creative permission to adjust arrangement for professional-quality results.' : ''}
${activeMode === 'B' ? '* In Mode B: every moodboard item must appear. AI-generated completions must share the moodboard\'s design DNA. Objects must have full 3D volume, realistic shadows, and correct human-world dimensions.' : ''}
${activeMode === 'C' ? '* In Mode C: the room reference\'s spatial structure and perspective are fully honoured. The moodboard\'s materials and furniture are applied onto this structure. Minor positional refinements are permitted to achieve aesthetic harmony.' : ''}
* Maintain physical and spatial plausibility at all times. Furniture must respect gravity, wall boundaries, door clearances, and walkable pathways.
* The result must feel like a professionally photographed interior — spatially logical, dimensionally accurate, aesthetically elevated, and compositionally intentional.
* applyRoomLayout — ${applyRoomLayout}. If true: Mode C is active (full spatial integration). If false (default): Mode A is active (flexible replacement). If referenceImages are not uploaded, Mode B is active regardless of this toggle.
* If any input is missing or '--', skip it silently without generating filler content.
* No explanations, no formatting, no headings in output.
* selectedModel — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.

Output dimensions: ${dimensions.width}x${dimensions.height} pixels (${selectedAspectRatio} aspect ratio)`;

      const baseInstruction = "Generate exactly what the prompt describes. Do not add style, materials, or aesthetic elements not mentioned in the prompt. Render with hyper realism and photorealism quality.";
      const fullPrompt = `${baseInstruction}\n\n${moodboardSystemPrompt}`;

      // Route to appropriate service based on selected model
      let generatedImageUrl;

      if (isFluxModel(selectedModel)) {
        // Use Flux service for Flux Pro / Flux Flex models
        generatedImageUrl = await generateWithFlux({
          modelName: selectedModel,
          directPromptText: fullPrompt,
          userPromptText: promptText,
          additionalPromptText: additionalPromptText,
          moodboardImages: allMoodboardImages,
          referenceImages: referenceImages,
          roomType: selectedRoomType === 'Custom' ? '' : selectedRoomType,
          selectedAngleShot: selectedAngleShot,
          selectedRoomSize: selectedRoomSize,
          imageSize: selectedAspectRatio,
          signal: abortControllerRef?.current?.signal,
        });
      } else {
        // Use Gemini service for Gemini models
        generatedImageUrl = await generateMoodboardTo3D({
          referenceImages: referenceImages,
          moodboardImages: allMoodboardImages,
          userPromptText: promptText,
          additionalPromptText: additionalPromptText,
          toolSystemPrompt: fullPrompt,
          roomType: selectedRoomType === 'Custom' ? '' : selectedRoomType,
          materialImages: materialImages,
          materialTextureEnhancement: false,
          imageSize: selectedAspectRatio,
          modelName: selectedModel,
          dimensions: dimensions,
          applyRoomLayout: applyRoomLayout,
          selectedRoomSize: selectedRoomSize,
          signal: abortControllerRef?.current?.signal,
        });
      }

      console.log(
        "Image generated successfully:",
        generatedImageUrl?.substring(0, 50) + "...",
      );

      // Update the current image with actual generated image from Gemini API
      setCurrentImage(generatedImageUrl);
      setGeneratedImages((prev) => [generatedImageUrl, ...prev?.slice(0, 3)]);
      // ไม่เพิ่มผลลัพธ์เข้า libraryAssets เพราะไม่ควรใช้เป็น input ของ generation ถัดไป
      showSaveModal(generatedImageUrl, location.pathname);

      // Save to database using session user
      await saveGenerationToDatabase(
        generatedImageUrl,
        promptText,
        currentUser,
      );

      // Deduct credits using session user
      await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(selectedModel));
    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes("cancelled")) {
        setCancelled();
      } else {
        console.error("Error in generation workflow:", error);
        setFailed();
        setGenerateError(
          error?.message || "Failed to generate image. Please try again.",
        );
      }
    } finally {
      clearTimer();
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const saveGenerationToDatabase = async (
    imageUrl,
    prompt,
    currentUser = null,
  ) => {
    // Use passed currentUser or fallback to AuthContext user
    const activeUser = currentUser || user;

    if (!activeUser) {
      console.error("[SAVE ERROR] No active user found");
      return;
    }

    console.log("[SAVE DEBUG] Saving generation for user:", activeUser?.id);

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl; // Use storage URL for all DB saves

      // Create or update project
      let projectId = currentProjectId;

      if (!projectId) {
        const projectName = `${selectedRoomType || "Room"} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } =
          await projectsService?.createProject(activeUser?.id, {
            name: projectName,
            description: prompt,
            thumbnail: imageUrl,
            toolType: "moodboard-to-3d",
            toolName: "Moodboard to 3D Room",
            status: "completed",
            tags: [selectedRoomType, selectedModel]?.filter(Boolean),
            variations: 1,
          });

        if (projectError) {
          console.error("Error creating project:", projectError);
          return;
        }

        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        // Increment variations and update thumbnail to latest image
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      // Save design
      const { data: design, error: designError } =
        await savedDesignsService?.createDesign(activeUser?.id, projectId, {
          imageUrl,
          prompt,
          isFavorite: false,
        });

      if (designError) {
        console.error("Error saving design:", designError);
        return;
      }

      setCurrentDesignId(design?.id);
      setGeneratedDesigns((prev) => [design, ...prev]);

      // Save generation metadata — only use columns that exist in generation_metadata table
      await generationMetadataService?.createMetadata(
        activeUser?.id,
        design?.id,
        {
          modelUsed: selectedModel,
          imageSize: selectedAspectRatio,
          roomType: (selectedRoomType === 'Custom' ? '' : selectedRoomType) || "Room",
          angleView: selectedAngleShot || "Top View",
          style: "Generated",
          resolution: `${getImageDimensions()?.width}x${getImageDimensions()?.height}`,
          cameraView: selectedAngleShot || "Top View",
          lighting: "Auto",
          quality: "High",
          usedMedia: moodboardImages,
          additionalParams: {
            imageWidth: getImageDimensions()?.width,
            imageHeight: getImageDimensions()?.height,
            aspectRatio: selectedAspectRatio,
            selectedResolution: selectedResolution || "1K",
            selectedRoomSize: selectedRoomSize || null,
            promptText: promptText || "",
            additionalPromptText: additionalPromptText || "",
            refinedPrompt: prompt || "",
          },
        },
      );

      console.log("Generation saved to database successfully");
    } catch (error) {
      console.error("Error saving to database:", error);
    }
  };

  const handleSaveProject = async () => {
    if (!user) {
      alert("Please sign in to save projects.");
      return;
    }

    if (!currentImage) {
      alert("No image to save. Please generate an image first.");
      return;
    }

    setIsSaving(true);
    try {
      await saveGenerationToDatabase(currentImage, refinedPrompt || promptText);
      alert("Project saved successfully!");
    } catch (error) {
      console.error("Error saving project:", error);
      alert("Failed to save project. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef?.current) {
      abortControllerRef?.current?.abort();
      setCancelled();
      setIsGenerating(false);
      setGenerateError("Generation cancelled by user.");
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef?.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef?.current?.scrollHeight + "px";
    }
  }, [promptText]);

  // Auto-resize additional textarea
  useEffect(() => {
    if (additionalTextareaRef?.current) {
      additionalTextareaRef.current.style.height = "auto";
      additionalTextareaRef.current.style.height =
        additionalTextareaRef?.current?.scrollHeight + "px";
    }
  }, [additionalPromptText]);

  // Close resolution dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktop = resolutionRef.current && resolutionRef.current.contains(event.target);
      const inMobile = resolutionMobileRef.current && resolutionMobileRef.current.contains(event.target);
      if (!inDesktop && !inMobile) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add this block - Define missing handler functions
  const handleDownload = async () => {
    if (!currentImage) return;

    setButtonLoading("download");
    try {
      const response = await fetch(currentImage);
      const blob = await response?.blob();
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `room-design-${Date.now()}.png`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
    } finally {
      setButtonLoading(null);
    }
  };

  const handleEditClick = () => {
    if (!currentImage) return;
    setButtonLoading("edit");
    // Navigate to editor or open edit modal
    navigate("/editor", { state: { imageUrl: currentImage } });
    setButtonLoading(null);
  };

  const handleDeleteClick = () => {
    if (!currentImage) return;
    setCurrentImage(null);
    setGeneratedImages((prev) => prev?.filter((img) => img !== currentImage));
  };

  const handleRefresh = async () => {
    if (!currentImage) return;

    setIsRegenerating(true);
    setButtonLoading("refresh");
    try {
      await handleGenerate();
    } catch (error) {
      console.error("Error regenerating image:", error);
    } finally {
      setIsRegenerating(false);
      setButtonLoading(null);
    }
  };

  // FIXED: Drag and drop handlers with better error handling and data validation
  const handleDragStart = (e, imageUrl, type) => {
    if (!imageUrl || !type) {
      console.error("Invalid drag data:", { imageUrl, type });
      return;
    }

    try {
      e.dataTransfer.effectAllowed = "copy";
      e?.dataTransfer?.setData("text/plain", imageUrl); // Fallback for compatibility
      e?.dataTransfer?.setData("imageUrl", imageUrl);
      e?.dataTransfer?.setData("imageType", type);
      console.log("Drag started:", { imageUrl, type });
    } catch (error) {
      console.error("Error in handleDragStart:", error);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e, targetZone) => {
    e?.preventDefault();

    try {
      // Try multiple data formats for better compatibility
      let imageUrl =
        e?.dataTransfer?.getData("imageUrl") ||
        e?.dataTransfer?.getData("text/plain") ||
        e?.dataTransfer?.getData("text");

      let imageType = e?.dataTransfer?.getData("imageType");

      console.log("Drop event:", { imageUrl, imageType, targetZone });

      if (!imageUrl) {
        console.warn("No image URL found in drop event");
        return;
      }

      if (targetZone === "moodboard") {
        setMoodboardImages((prev) => {
          if (!prev?.includes(imageUrl)) {
            return [...prev, imageUrl];
          }
          return prev;
        });
      } else if (targetZone === "reference") {
        setReferenceImages((prev) => {
          if (!prev?.includes(imageUrl)) {
            return [...prev, imageUrl];
          }
          return prev;
        });
      } else if (targetZone === "prompt") {
        setDraggedImages((prev) => {
          if (!prev?.includes(imageUrl)) {
            return [...prev, imageUrl];
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error in handleDrop:", error);
    }
  };

  const removeFromPromptZone = async (imageUrl, type) => {
    if (type === "generated") {
      setDraggedImages((prev) => prev?.filter((img) => img !== imageUrl));
    } else if (type === "library") {
      const updated = libraryAssets?.filter((img) => img !== imageUrl);
      setLibraryAssets(updated);
      for (const design of generatedDesigns) {
        if (design?.id) {
          await generationMetadataService.updateUsedMedia(design.id, updated);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-black overflow-x-hidden">
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Header
        showCredits={true}
        remainingCredits={userProfile?.remainingCredits || 0}
        monthlyLimit={userProfile?.monthlyCredits || 100}
      />
      <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
        {/* Narrow Fixed-Width Centered Container for Desktop/Laptop/Tablet */}
        <div className="max-w-[1400px] mx-auto">
          {/* Desktop Header — aligned to content columns */}
          <div className="hidden md:flex gap-4 justify-center mb-6">
            <div className="w-[400px] flex-shrink-0">
              <button
                onClick={() => navigate("/main-dashboard")}
                className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4"
              >
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
              <h1 className="text-3xl font-bold text-white">
                Moodboard to Room
              </h1>
            </div>
            <div className="flex-1 max-w-[900px] flex items-end justify-end gap-3 flex-shrink min-w-0">
              <button
                onClick={() => openGuide()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
              >
                <Icon name="HelpCircle" size={18} />
                Virtual Tool Guide
              </button>
              <button
                onClick={() => navigate("/project-history")}
                className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform whitespace-nowrap flex-shrink-0"
              >
                <Icon name="History" size={18} />
                View Projects
              </button>
            </div>
          </div>

          {/* Mobile Header */}
          <div className="md:hidden mb-6">
            <button
              onClick={() => navigate("/main-dashboard")}
              className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
            <div className="flex items-center justify-between gap-2">
              <h1 className="text-3xl font-bold text-white">
                Moodboard to Room
              </h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openGuide()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                >
                  <Icon name="HelpCircle" size={16} />
                  Guide
                </button>
                <button
                  onClick={() => navigate("/project-history")}
                  className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0"
                >
                  <Icon name="History" size={18} />
                  <span className="hidden md:inline">View Projects</span>
                </button>
              </div>
            </div>
          </div>

          {/* Desktop/Tablet/Laptop Layout (hidden on mobile) */}
          <div className="hidden md:flex gap-4 justify-center">
            {/* Left Panel - Upload Windows Stacked Vertically (First Third) */}
            <div className="w-[400px] flex-shrink-0 space-y-4">
              {/* Upload Counter */}
              <div className="text-left">
                <span className={`text-sm font-medium ${isUploadLimitReached ? "text-red-400" : "text-white/70"}`}>
                  {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
                </span>
              </div>
              {/* Moodboard Images Upload */}
              <div
                ref={moodboardUploadDesktopRef}
                className="glassmorphic-card rounded-2xl p-3 border border-transparent transition-all duration-200"
                style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; e.currentTarget.classList.add("border-orange-500/50"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("border-orange-500/50"); }}
                onDrop={(e) => { e.currentTarget.classList.remove("border-orange-500/50"); handleDrop(e, "moodboard"); }}
              >
                <h3 className="text-white text-sm font-medium mb-2">
                  Moodboard <br />
                  image(s)<span className="text-red-500">*</span>
                </h3>

                <label className={`block ${isUploadLimitReached ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleMoodboardUpload}
                    className="hidden"
                    disabled={isUploadLimitReached}
                  />

                  <div className="glassmorphic-upload-zone rounded-2xl p-7 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                    <Icon
                      name="Image"
                      size={36}
                      className="text-orange-500 mx-auto mb-2.5"
                    />
                    <p className="text-white/70 text-sm">
                      Upload image PNG or JPG
                    </p>
                  </div>
                </label>
                {moodboardImages?.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {moodboardImages?.map((img, idx) => (
                      <UploadedImageThumb
                        key={idx}
                        src={img}
                        alt={`Moodboard ${idx + 1}`}
                        onRemove={() => handleRemoveMoodboard(idx)}
                        imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                        height="h-auto"
                        className=""
                        previewWidth="w-[200px]"
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Reference Room Layout Upload */}
              <div
                ref={roomLayoutDesktopRef}
                className="glassmorphic-card rounded-2xl p-5 border border-transparent transition-all duration-200"
                style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; e.currentTarget.classList.add("border-orange-500/50"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("border-orange-500/50"); }}
                onDrop={(e) => { e.currentTarget.classList.remove("border-orange-500/50"); handleDrop(e, "reference"); }}
              >
                <h3 className="text-white text-sm font-medium mb-3.5">
                  Room Layout Reference
                </h3>

                <label className={`block ${isUploadLimitReached ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleReferenceUpload}
                    className="hidden"
                    disabled={isUploadLimitReached}
                  />

                  <div className="glassmorphic-upload-zone rounded-2xl p-7 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                    <Icon
                      name="Image"
                      size={36}
                      className="text-orange-500 mx-auto mb-2.5"
                    />
                    <p className="text-white/70 text-sm">
                      Upload image PNG or JPG
                    </p>
                  </div>
                </label>
                {referenceImages?.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {referenceImages?.map((img, idx) => (
                      <UploadedImageThumb
                        key={idx}
                        src={img}
                        alt={`Reference ${idx + 1}`}
                        onRemove={() => handleRemoveReference(idx)}
                        imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                        height="h-auto"
                        className=""
                        previewWidth="w-[200px]"
                      />
                    ))}
                  </div>
                )}
                {/* Apply Room Layout toggle */}
                <div ref={applyToggleDesktopRef} className="flex items-center justify-between mt-3 rounded-xl px-2 py-1.5 transition-all duration-200" style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <span className={`text-xs ${applyRoomLayout ? 'text-white' : 'text-white/50'}`}>Apply Room Layout design onto final generation</span>
                  <button
                    onClick={() => setApplyRoomLayout(!applyRoomLayout)}
                    className={`relative w-10 h-5 rounded-full transition-colors duration-300 flex-shrink-0 ${applyRoomLayout ? 'bg-orange-500' : 'bg-white/20'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300 ${applyRoomLayout ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              {/* Room Size */}
              <div ref={roomSizeDesktopRef} className="glassmorphic-card rounded-3xl p-5" style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div className="mb-3 px-2">
                  <span className="text-white/70 text-sm">Room size</span>
                </div>
                <div className="space-y-2">
                  {[roomSizes.slice(0, 2), roomSizes.slice(2, 4)].map((row, ri) => (
                    <div key={ri} className="grid grid-cols-2 gap-2">
                      {row.map((rs) => (
                        <button
                          key={rs.id}
                          onClick={() => setSelectedRoomSize(rs.id)}
                          className={`rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all py-3 px-4 ${
                            selectedRoomSize === rs.id
                              ? "bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]"
                              : "bg-white/5 border border-white/10 hover:border-orange-500/30"
                          }`}
                        >
                          <span className="text-white font-semibold text-xs">{rs.label}</span>
                          <span className="text-white/90 text-[10px]">{rs.size}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Asset Library - Below Upload Windows */}
              <div ref={assetLibraryDesktopRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <AssetLibraryPanel
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  generatedImages={generatedImages}
                  setGeneratedImages={setGeneratedImages}
                  onImageClick={(img) => setCurrentImage(img)}
                  onImageDeleted={(remaining) => {
                    if (!isGenerating) setCurrentImage(remaining[0] || null);
                  }}
                  showDragHint={true}
                  currentDesignId={currentDesignId}
                  currentProjectId={currentProjectId}
                />
              </div>
            </div>

            {/* Center Panel - Main Generation Area - Expanded to fill removed right sidebar space */}
            <div className="flex-1 max-w-[900px] space-y-4" style={showGuide ? { position: 'relative', zIndex: 1 } : {}}>
              {/* Main Image Display - Wider */}
              <div ref={resultDesktopRef} className="glassmorphic-card rounded-3xl p-4 relative" style={{ position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, padding: showGuide && guideStep === 12 ? 'calc(1rem + 10px)' : undefined, margin: showGuide && guideStep === 12 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : undefined, transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <div className="flex justify-end items-center mb-3 px-2">
                  <span className="text-white/70 text-sm">Result</span>
                </div>
                <div className="relative rounded-2xl overflow-hidden bg-black/40 h-[550px]">
                  {(isRefining || isGenerating) ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon
                          name="Loader"
                          size={48}
                          className="text-orange-500 mx-auto mb-3 animate-spin"
                        />
                        <p className="text-white text-sm font-medium">
                          {isRefining
                            ? "Refining prompt with AI..."
                            : "Generating your 3D room..."}
                        </p>
                        <p className="text-white/60 text-xs mt-1">
                          This may take a moment
                        </p>
                      </div>
                    </div>
                  ) : currentImage ? (
                    <img
                      src={currentImage}
                      alt="Generated room design"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon
                          name="Image"
                          size={48}
                          className="text-white/30 mx-auto mb-3"
                        />
                        <p className="text-white/50 text-sm">
                          No image generated yet
                        </p>
                        <p className="text-white/30 text-xs mt-1">
                          Upload images and click Generate
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Icon buttons overlay - standardized from cad-mood-room-generator */}
                  <div className="absolute bottom-4 right-4 flex items-center space-x-2">
                    <button
                      onClick={handleDownload}
                      disabled={!currentImage || buttonLoading !== null}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download"
                    >
                      {buttonLoading === "download" ? (
                        <Icon
                          name="Loader"
                          size={18}
                          className="text-white animate-spin"
                        />
                      ) : (
                        <Icon
                          name="Download"
                          size={18}
                          className="text-white"
                        />
                      )}
                    </button>
                    <button
                      onClick={handleDeleteClick}
                      disabled={!currentImage || buttonLoading !== null}
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={18} className="text-white" />
                    </button>
                    <button
                      onClick={handleRefresh}
                      disabled={
                        !currentImage ||
                        buttonLoading !== null ||
                        isRegenerating
                      }
                      className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Regenerate"
                    >
                      {buttonLoading === "refresh" || isRegenerating ? (
                        <Icon
                          name="Loader"
                          size={18}
                          className="text-white animate-spin"
                        />
                      ) : (
                        <Icon
                          name="RefreshCw"
                          size={18}
                          className="text-white"
                        />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              {/* Prompt Task Bar - Simplified without Prompt section, Trained style, and Draw */}
              <div className="glassmorphic-card rounded-3xl p-6 border border-orange-500/20 shadow-[0_0_30px_rgba(249,115,22,0.15)]">
                {/* First Level: Plus Button + Textarea Field */}
                <div className="flex items-start gap-3 mb-6">
                  {/* Upload Image Icon */}
                  <label className={`flex-shrink-0 ${isUploadLimitReached ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      disabled={isUploadLimitReached}
                      onChange={async (e) => {
                        const files = Array.from(e?.target?.files);
                        const totalCurrent = moodboardImages.length + referenceImages.length;
                        const remaining = Math.max(0, MAX_TOTAL_UPLOADS - totalCurrent);
                        if (remaining === 0) return;
                        const limitedFiles = files.slice(0, remaining);
                        const urls = await Promise.all(
                          limitedFiles.map((file) => storageService.uploadFile(file, user?.id))
                        );
                        setDraggedImages((prev) => [...prev, ...urls.filter(Boolean)]);
                      }}
                    />

                    <div className="w-12 h-12 glassmorphic-button rounded-2xl flex items-center justify-center hover:scale-105 transition-transform">
                      <Icon name="Plus" size={20} className="text-white" />
                    </div>
                  </label>

                  {/* Textarea Field with auto-resize and drop zone */}
                  <div
                    className="flex-1 relative"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, "prompt")}
                  >
                    <textarea
                      ref={textareaRef}
                      value={promptText}
                      onChange={(e) => setPromptText(e?.target?.value)}
                      placeholder="Add furniture or materials images, or additional text request. Drag and drop images here."
                      rows={5}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl text-white text-xs placeholder:text-white/40 focus:outline-none focus:border-orange-500/50 px-3 py-2 resize-none overflow-y-auto transition-all min-h-[120px] max-h-[300px]"
                      style={{ height: "120px" }}
                    />

                    {/* Display dragged images */}
                    {draggedImages?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {draggedImages?.map((img, idx) => (
                          <UploadedImageThumb
                            key={`dragged-${idx}`}
                            src={img}
                            alt={`Dragged ${idx + 1}`}
                            onRemove={() => removeFromPromptZone(img, "generated")}
                            imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10"
                            height="h-auto"
                            previewWidth="w-[200px]"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Second Level: Room Type & Angle View - Full Width */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {/* Room Type Dropdown */}
                  <div ref={roomTypeDesktopRef} className="rounded-xl" style={{ position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, padding: showGuide && guideStep === 6 ? '10px' : undefined, margin: showGuide && guideStep === 6 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <RoomTypeDropdown
                      value={selectedRoomType}
                      onChange={(val) => setSelectedRoomType(val)}
                    />
                  </div>

                  {/* Angle Shot Dropdown */}
                  <div ref={angleViewDesktopRef} className="rounded-xl" style={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, padding: showGuide && guideStep === 7 ? '10px' : undefined, margin: showGuide && guideStep === 7 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <AngleViewDropdown value={selectedAngleShot} onChange={(val) => setSelectedAngleShot(val)} />
                  </div>
                </div>

                {/* Third Level: Model Selection, Image Size, Generate Button */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    {/* Model Selection */}
                    <div ref={modelDesktopRef} className="w-1/2 min-w-0 rounded-xl" style={{ position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, padding: showGuide && guideStep === 8 ? '10px' : undefined, margin: showGuide && guideStep === 8 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      <ModelSelectionDropdown
                        value={selectedModel}
                        onChange={setSelectedModel}
                      />
                    </div>

                    {/* Aspect Ratio */}
                    <div className="w-1/2 min-w-0" ref={(el) => { aspectRatioRef.current = el; aspectRatioDesktopRef.current = el; }} style={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, borderRadius: '0.75rem', padding: showGuide && guideStep === 9 ? '10px' : undefined, margin: showGuide && guideStep === 9 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      <label className="text-white/70 text-xs mb-2 block">
                        Aspect Ratio
                      </label>
                      <div className="relative">
                        {/* Dropdown trigger */}
                        <button
                          onClick={() =>
                            setIsAspectRatioOpen(!isAspectRatioOpen)
                          }
                          className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <AspectRatioIcon
                              ratio={selectedAspectRatio}
                              size={18}
                              className="text-orange-500"
                            />
                            <span className="text-white text-sm">
                              {selectedAspectRatio}
                            </span>
                          </div>
                          <Icon
                            name={isAspectRatioOpen ? "ChevronUp" : "ChevronDown"}
                            size={16}
                            className="text-white/50"
                          />
                        </button>

                        {/* Dropdown menu */}
                        {isAspectRatioOpen && (
                          <div
                            className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                            style={{
                              backgroundColor: "rgba(0, 0, 0, 0.95)",
                              backdropFilter: "blur(8px)",
                            }}
                          >
                            {aspectRatios?.map((ratio) => (
                              <button
                                key={ratio?.value}
                                onClick={() => {
                                  setSelectedAspectRatio(ratio?.value);
                                  setIsAspectRatioOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                  selectedAspectRatio === ratio?.value
                                    ? "bg-orange-500/20 hover:bg-orange-500/30"
                                    : "hover:bg-white/5"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <AspectRatioIcon
                                    ratio={ratio?.value}
                                    size={18}
                                    className={
                                      selectedAspectRatio === ratio?.value
                                        ? "text-orange-500"
                                        : "text-white/50"
                                    }
                                  />
                                  <span
                                    className={`text-sm ${
                                      selectedAspectRatio === ratio?.value
                                        ? "text-white font-medium"
                                        : "text-white/70"
                                    }`}
                                  >
                                    {ratio?.label}
                                  </span>
                                </div>

                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Resolution Selection */}
                  <div className="flex items-center gap-3">
                    <div className="w-1/2 min-w-0" ref={(el) => { resolutionRef.current = el; resolutionGuideDesktopRef.current = el; }} style={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, borderRadius: '0.75rem', padding: showGuide && guideStep === 10 ? '10px' : undefined, margin: showGuide && guideStep === 10 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                      {!isFluxModel(selectedModel) && (
                      <>
                      <label className="text-white/70 text-xs mb-2 block">
                        Resolution
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                          className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
                        >
                          <span className="text-white text-sm font-medium">{selectedResolution}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white/50 text-xs">
                                {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                              </span>
                            <Icon
                              name={isResolutionOpen ? "ChevronUp" : "ChevronDown"}
                              size={16}
                              className="text-white/50"
                            />
                          </div>
                        </button>
                        {isResolutionOpen && (
                          <div
                            className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                            style={{
                              backgroundColor: "rgba(0, 0, 0, 0.95)",
                              backdropFilter: "blur(8px)",
                            }}
                          >
                            {RESOLUTION_OPTIONS.map((resKey) => {
                              const dims = getResolutionDimensions(resKey);
                              return (
                                <button
                                  key={resKey}
                                  onClick={() => {
                                    setSelectedResolution(resKey);
                                    setIsResolutionOpen(false);
                                  }}
                                  className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                    selectedResolution === resKey
                                      ? "bg-orange-500/20 hover:bg-orange-500/30"
                                      : "hover:bg-white/5"
                                  }`}
                                >
                                  <span
                                    className={`text-sm ${
                                      selectedResolution === resKey
                                        ? "text-white font-medium"
                                        : "text-white/70"
                                    }`}
                                  >
                                    {resKey}
                                  </span>
                                  <span
                                      className={`text-xs ${
                                        selectedResolution === resKey
                                          ? "text-orange-400"
                                          : "text-white/50"
                                      }`}
                                    >
                                      {dims.width}×{dims.height}
                                    </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      </>
                      )}
                    </div>
                  </div>

                  {/* Prompt Input Field - Between Model/Image Size and Generate Button */}
                  {selectedRoomType === 'Custom' && (
                    <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                  )}
                  <textarea
                    ref={additionalTextareaRef}
                    value={additionalPromptText}
                    onChange={(e) => setAdditionalPromptText(e?.target?.value)}
                    placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Enter your prompt here...'}
                    rows={5}
                    className={`w-full bg-white/5 rounded-2xl text-white text-sm placeholder:text-white/40 focus:outline-none px-4 py-3 resize-none overflow-y-auto transition-all min-h-[120px] max-h-[300px] ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                    style={{ height: "120px" }}
                  />

                  {/* Generate Button */}
                  <div ref={generateBtnDesktopRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, padding: showGuide && guideStep === 11 ? '10px' : undefined, margin: showGuide && guideStep === 11 ? '-10px' : undefined, marginTop: showGuide && guideStep === 11 ? '20px' : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <button
                    onClick={handleGenerate}
                    disabled={
                      isGenerating ||
                      moodboardImages?.length === 0
                    }
                    className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGenerating ? (
                      <>
                        <Icon
                          name="Loader"
                          size={18}
                          className="animate-spin"
                        />
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-sparkles"
                        >
                          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                          <path d="M20 3v4"></path>
                          <path d="M22 5h-4"></path>
                          <path d="M4 17v2"></path>
                          <path d="M5 18H3"></path>
                        </svg>
                        Generate Image
                      </>
                    )}
                  </button>
                  </div>

                  {(isRefining || isGenerating) && (
                    <button
                      onClick={handleCancelGeneration}
                      className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                    >
                      <Icon name="X" size={16} />
                      Cancel
                    </button>
                  )}

                  {refineError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">
                      {refineError}
                    </div>
                  )}

                  {generateError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">
                      {generateError}
                    </div>
                  )}

                  {refinedPrompt && !refineError && !generateError && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                      <p className="text-green-400 text-xs font-medium mb-1">
                        ✓ Refined:
                      </p>
                      <p className="text-white/80 text-xs">{refinedPrompt}</p>
                    </div>
                  )}

                  <p className="text-white/50 text-xs text-center">
                    Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout (visible only on mobile) */}
          <div className="md:hidden space-y-4">
            {/* 1. Main Image Display - First */}
            <div ref={resultMobileRef} className="glassmorphic-card rounded-2xl p-3 relative" style={{ position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, padding: showGuide && guideStep === 12 ? 'calc(0.75rem + 10px)' : undefined, margin: showGuide && guideStep === 12 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : undefined, transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="flex justify-end items-center mb-2 px-1">
                <span className="text-white/70 text-xs">Result</span>
              </div>
              <div className="relative rounded-xl overflow-hidden bg-black/40 h-[280px]">
                {(isRefining || isGenerating) ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <Icon
                        name="Loader"
                        size={36}
                        className="text-orange-500 mx-auto mb-2 animate-spin"
                      />
                      <p className="text-white text-xs font-medium">
                        {isRefining ? "Refining..." : "Generating..."}
                      </p>
                    </div>
                  </div>
                ) : currentImage ? (
                  <img
                    src={currentImage}
                    alt="Generated room design"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <Icon
                        name="Image"
                        size={36}
                        className="text-white/30 mx-auto mb-2"
                      />
                      <p className="text-white/50 text-xs">
                        No image generated yet
                      </p>
                      <p className="text-white/30 text-xs mt-1">
                        Upload and generate
                      </p>
                    </div>
                  </div>
                )}

                <div className="absolute bottom-3 right-3 flex gap-1.5">
                  <button
                    className="glassmorphic-icon-button rounded-lg p-2 hover:scale-105 transition-transform"
                    title="Download"
                  >
                    <Icon name="Download" size={16} className="text-white" />
                  </button>
                  <button
                    className="glassmorphic-icon-button rounded-lg p-2 hover:scale-105 transition-transform"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={16} className="text-white" />
                  </button>
                  <button
                    className="glassmorphic-icon-button rounded-lg p-2 hover:scale-105 transition-transform"
                    title="Recreate"
                  >
                    <Icon name="RefreshCw" size={16} className="text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* 2. Moodboard and Reference Uploads - Second (Paired) - Moved between Result and Prompt */}
            {/* Mobile Upload Counter */}
            <div className="text-left">
              <span className={`text-sm font-medium ${isUploadLimitReached ? "text-red-400" : "text-white/70"}`}>
                {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {/* Moodboard Images Upload */}
              <div ref={moodboardUploadMobileRef} className="glassmorphic-card rounded-2xl p-3" style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <h3 className="text-white text-sm font-medium mb-2">
                  Moodboard image(s)<span className="text-red-500">*</span>
                </h3>

                <label className={`block ${isUploadLimitReached ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleMoodboardUpload}
                    className="hidden"
                    disabled={isUploadLimitReached}
                  />

                  <div className="glassmorphic-upload-zone rounded-xl p-4 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                    <Icon
                      name="Image"
                      size={24}
                      className="text-orange-500 mx-auto mb-1.5"
                    />
                    <p className="text-white/70 text-sm">Upload PNG or JPG</p>
                  </div>
                </label>
                {moodboardImages?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {moodboardImages?.map((img, idx) => (
                      <UploadedImageThumb
                        key={idx}
                        src={img}
                        alt={`Moodboard ${idx + 1}`}
                        onRemove={() => handleRemoveMoodboard(idx)}
                        imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10"
                        height="h-auto"
                        className=""
                        previewWidth="w-[200px]"
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Reference Room Layout Upload */}
              <div ref={roomLayoutMobileRef} className="glassmorphic-card rounded-2xl p-3" style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <h3 className="text-white text-sm font-medium mb-2">
                  Room Layout Reference
                </h3>

                <label className={`block ${isUploadLimitReached ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg"
                    onChange={handleReferenceUpload}
                    className="hidden"
                    disabled={isUploadLimitReached}
                  />

                  <div className="glassmorphic-upload-zone rounded-xl p-4 text-center border-2 border-dashed border-white/20 hover:border-orange-500/40 transition-all duration-300">
                    <Icon
                      name="Image"
                      size={24}
                      className="text-orange-500 mx-auto mb-1.5"
                    />
                    <p className="text-white/70 text-sm">Upload PNG or JPG</p>
                  </div>
                </label>
                {referenceImages?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {referenceImages?.map((img, idx) => (
                      <UploadedImageThumb
                        key={idx}
                        src={img}
                        alt={`Reference ${idx + 1}`}
                        onRemove={() => handleRemoveReference(idx)}
                        imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10"
                        height="h-auto"
                        className=""
                        previewWidth="w-[200px]"
                      />
                    ))}
                  </div>
                )}
                {/* Apply Room Layout toggle - Mobile */}
                <div ref={applyToggleMobileRef} className="flex items-center justify-between mt-3 rounded-xl px-2 py-1.5 transition-all duration-200" style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <span className={`text-xs ${applyRoomLayout ? 'text-white' : 'text-white/50'}`}>Apply Room Layout design onto final generation</span>
                  <button
                    onClick={() => setApplyRoomLayout(!applyRoomLayout)}
                    className={`relative w-10 h-5 rounded-full transition-colors duration-300 flex-shrink-0 ${applyRoomLayout ? 'bg-orange-500' : 'bg-white/20'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300 ${applyRoomLayout ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Asset Library - Between uploads and Room Size */}
            <div ref={assetLibraryMobileRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <AssetLibraryPanel
                libraryAssets={libraryAssets}
                setLibraryAssets={setLibraryAssets}
                generatedImages={generatedImages}
                setGeneratedImages={setGeneratedImages}
                onImageClick={(img) => setCurrentImage(img)}
                onImageDeleted={(remaining) => {
                  if (!isGenerating) setCurrentImage(remaining[0] || null);
                }}
                showDragHint={true}
                currentDesignId={currentDesignId}
                currentProjectId={currentProjectId}
              />
            </div>

            {/* Room Size - Mobile */}
            <div ref={roomSizeMobileRef} className="glassmorphic-card rounded-2xl p-3" style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
              <div className="mb-2 px-1">
                <span className="text-white/70 text-sm">Room size</span>
              </div>
              <div className="space-y-2">
                {[roomSizes.slice(0, 2), roomSizes.slice(2, 4)].map((row, ri) => (
                  <div key={ri} className="grid grid-cols-2 gap-2">
                    {row.map((rs) => (
                      <button
                        key={rs.id}
                        onClick={() => setSelectedRoomSize(rs.id)}
                        className={`rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all py-2.5 px-3 ${
                          selectedRoomSize === rs.id
                            ? "bg-white/5 border border-orange-500/50 shadow-[0_0_20px_rgba(254,131,56,0.15)]"
                            : "bg-white/5 border border-white/10 hover:border-orange-500/30"
                        }`}
                      >
                        <span className="text-white font-semibold text-xs">{rs.label}</span>
                        <span className="text-white/90 text-[10px]">{rs.size}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Prompt Task Bar for Mobile - Simplified */}
            <div className="glassmorphic-card rounded-2xl p-3 border border-orange-500/20 shadow-[0_0_20px_rgba(249,115,22,0.15)]">
              {/* First Level: Plus Button + Textarea Field */}
              <div className="flex items-start gap-2 mb-6">
                {/* Upload Image Icon */}
                <label className={`flex-shrink-0 ${isUploadLimitReached ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    disabled={isUploadLimitReached}
                    onChange={async (e) => {
                      const files = Array.from(e?.target?.files);
                      const totalCurrent = moodboardImages.length + referenceImages.length;
                      const remaining = Math.max(0, MAX_TOTAL_UPLOADS - totalCurrent);
                      if (remaining === 0) return;
                      const limitedFiles = files.slice(0, remaining);
                      const urls = await Promise.all(
                        limitedFiles.map((file) => storageService.uploadFile(file, user?.id))
                      );
                      setLibraryAssets((prev) => [...prev, ...urls.filter(Boolean)]);
                    }}
                  />

                  <div className="w-8 h-8 glassmorphic-button rounded-xl flex items-center justify-center hover:scale-105 transition-transform">
                    <Icon name="Plus" size={14} className="text-white" />
                  </div>
                </label>

                {/* Textarea Field with auto-resize */}
                <textarea
                  ref={textareaRef}
                  value={promptText}
                  onChange={(e) => setPromptText(e?.target?.value)}
                  placeholder="Add furniture or materials images, or additional text request"
                  rows={5}
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl text-white text-xs placeholder:text-white/40 focus:outline-none focus:border-orange-500/50 px-3 py-2 resize-none overflow-y-auto transition-all min-h-[120px] max-h-[300px]"
                  style={{ height: "120px" }}
                />
              </div>

              {/* Second Level: Room Type & Angle View */}
              <div className="space-y-2 mb-2">
                {/* Room Type Dropdown */}
                <div ref={roomTypeMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, padding: showGuide && guideStep === 6 ? '10px' : undefined, margin: showGuide && guideStep === 6 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <RoomTypeDropdown
                    value={selectedRoomType}
                    onChange={(val) => setSelectedRoomType(val)}
                    mobile
                  />
                </div>

                {/* Angle Shot Dropdown */}
                <div ref={angleViewMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, padding: showGuide && guideStep === 7 ? '10px' : undefined, margin: showGuide && guideStep === 7 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <AngleViewDropdown value={selectedAngleShot} onChange={(val) => setSelectedAngleShot(val)} mobile />
                </div>
              </div>

              {/* Third Level: Model Selection, Image Size, Generate Button */}
              <div className="space-y-3">
                {/* Model Selection - full width */}
                <div ref={modelMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, padding: showGuide && guideStep === 8 ? '10px' : undefined, margin: showGuide && guideStep === 8 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                  <ModelSelectionDropdown
                    value={selectedModel}
                    onChange={setSelectedModel}
                    mobile
                  />
                </div>

                {/* Aspect Ratio - Dropdown style (same as desktop) - full width */}
                <div ref={aspectRatioMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, padding: showGuide && guideStep === 9 ? '10px' : undefined, margin: showGuide && guideStep === 9 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <label className="text-white/70 text-xs mb-1 block">
                      Aspect Ratio
                    </label>
                    <div className="relative">
                      <button
                        onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                        className="w-full glassmorphic-select rounded-xl px-3 h-[38px] bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <AspectRatioIcon
                            ratio={selectedAspectRatio}
                            size={14}
                            className="text-orange-500"
                          />
                          <span className="text-white text-xs">
                            {selectedAspectRatio}
                          </span>
                        </div>
                        <Icon
                          name={isAspectRatioOpen ? "ChevronUp" : "ChevronDown"}
                          size={14}
                          className="text-white/50"
                        />
                      </button>

                      {isAspectRatioOpen && (
                        <div
                          className="absolute z-50 w-full bottom-full mb-2 rounded-xl border border-white/10 shadow-2xl overflow-hidden max-h-60 overflow-y-auto"
                          style={{
                            backgroundColor: "rgba(0, 0, 0, 0.95)",
                            backdropFilter: "blur(8px)",
                          }}
                        >
                          {aspectRatios?.map((ratio) => (
                            <button
                              key={ratio?.value}
                              onClick={() => {
                                setSelectedAspectRatio(ratio?.value);
                                setIsAspectRatioOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2.5 transition-all border-b border-white/5 last:border-b-0 ${
                                selectedAspectRatio === ratio?.value
                                  ? "bg-orange-500/20 hover:bg-orange-500/30"
                                  : "hover:bg-white/5"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <AspectRatioIcon
                                  ratio={ratio?.value}
                                  size={14}
                                  className={
                                    selectedAspectRatio === ratio?.value
                                      ? "text-orange-500"
                                      : "text-white/50"
                                  }
                                />
                                <span
                                  className={`text-xs ${
                                    selectedAspectRatio === ratio?.value
                                      ? "text-white font-medium"
                                      : "text-white/70"
                                  }`}
                                >
                                  {ratio?.label}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>


                {/* Resolution - full width */}
                <div ref={(el) => { resolutionMobileRef.current = el; resolutionGuideMobileRef.current = el; }} className="rounded-xl" style={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, padding: showGuide && guideStep === 10 ? '10px' : undefined, margin: showGuide && guideStep === 10 ? '-10px' : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    {!isFluxModel(selectedModel) && (
                    <>
                    <label className="text-white/70 text-xs mb-1 block">
                      Resolution
                    </label>
                    <div className="relative">
                      <button
                        onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                        className={`w-full glassmorphic-select rounded-xl px-3 h-[38px] bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}
                      >
                        <span className="text-white text-xs font-medium">{selectedResolution}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white/50 text-xs">
                              {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                            </span>
                          <Icon
                            name={isResolutionOpen ? "ChevronUp" : "ChevronDown"}
                            size={14}
                            className="text-white/50"
                          />
                        </div>
                      </button>
                      {isResolutionOpen && (
                        <div
                          className="absolute z-50 w-full bottom-full mb-2 rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                          style={{
                            backgroundColor: "rgba(0, 0, 0, 0.95)",
                            backdropFilter: "blur(8px)",
                          }}
                        >
                          {RESOLUTION_OPTIONS.map((resKey) => {
                            const dims = getResolutionDimensions(resKey);
                            return (
                              <button
                                key={resKey}
                                onClick={() => {
                                  setSelectedResolution(resKey);
                                  setIsResolutionOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2.5 transition-all border-b border-white/5 last:border-b-0 ${
                                  selectedResolution === resKey
                                    ? "bg-orange-500/20 hover:bg-orange-500/30"
                                    : "hover:bg-white/5"
                                }`}
                              >
                                <span
                                  className={`text-xs ${
                                    selectedResolution === resKey
                                      ? "text-white font-medium"
                                      : "text-white/70"
                                  }`}
                                >
                                  {resKey}
                                </span>
                                <span
                                    className={`text-xs ${
                                      selectedResolution === resKey
                                        ? "text-orange-400"
                                        : "text-white/50"
                                    }`}
                                  >
                                    {dims.width}×{dims.height}
                                  </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    </>
                    )}
                </div>

                {/* Prompt Input Field */}
                {selectedRoomType === 'Custom' && (
                  <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                )}
                <textarea
                  ref={additionalTextareaRef}
                  value={additionalPromptText}
                  onChange={(e) => setAdditionalPromptText(e?.target?.value)}
                  placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Enter your prompt here...'}
                  rows={5}
                  className={`w-full bg-white/5 rounded-xl text-white text-sm placeholder:text-white/40 focus:outline-none px-3 py-2 resize-none overflow-y-auto transition-all min-h-[120px] max-h-[300px] ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                  style={{ height: "120px" }}
                />

                {/* Generate Button */}
                <div ref={generateBtnMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, padding: showGuide && guideStep === 11 ? '10px' : undefined, margin: showGuide && guideStep === 11 ? '-10px' : undefined, marginTop: showGuide && guideStep === 11 ? '20px' : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1), padding 0.6s cubic-bezier(0.4, 0, 0.2, 1), margin 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    moodboardImages?.length === 0
                  }
                  className="w-full glassmorphic-button rounded-xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <Icon name="Loader" size={18} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-sparkles"
                      >
                        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                        <path d="M20 3v4"></path>
                        <path d="M22 5h-4"></path>
                        <path d="M4 17v2"></path>
                        <path d="M5 18H3"></path>
                      </svg>
                      Generate Image
                    </>
                  )}
                </button>
                </div>

                {(isRefining || isGenerating) && (
                  <button
                    onClick={handleCancelGeneration}
                    className="w-full glassmorphic-button rounded-xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                  >
                    <Icon name="X" size={16} />
                    Cancel
                  </button>
                )}

                {refineError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">
                    {refineError}
                  </div>
                )}

                {refinedPrompt && !refineError && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                    <p className="text-green-400 text-xs font-medium mb-1">
                      ✓ Refined:
                    </p>
                    <p className="text-white/80 text-xs">{refinedPrompt}</p>
                  </div>
                )}

                <p className="text-white/50 text-xs text-center">
                  Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
        <div
          className="fixed inset-0 z-[200]"
          onClick={closeGuide}
        >
          {/* Dim overlay */}
          <div
            className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
          />
        </div>

        {/* Tooltip bubble - outside overlay for proper z-index */}
        <div
          className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
            style={(() => {
              const desktopRefs = [moodboardUploadDesktopRef.current, roomLayoutDesktopRef.current, applyToggleDesktopRef.current, roomSizeDesktopRef.current, assetLibraryDesktopRef.current, roomTypeDesktopRef.current, angleViewDesktopRef.current, modelDesktopRef.current, aspectRatioDesktopRef.current, resolutionGuideDesktopRef.current, generateBtnDesktopRef.current, resultDesktopRef.current];
              const mobileRefs = [moodboardUploadMobileRef.current, roomLayoutMobileRef.current, applyToggleMobileRef.current, roomSizeMobileRef.current, assetLibraryMobileRef.current, roomTypeMobileRef.current, angleViewMobileRef.current, modelMobileRef.current, aspectRatioMobileRef.current, resolutionGuideMobileRef.current, generateBtnMobileRef.current, resultMobileRef.current];
              const desktopRef = desktopRefs[guideStep - 1];
              const mobileRef = mobileRefs[guideStep - 1];
              const ref = window.innerWidth >= 768 ? desktopRef : mobileRef;
              if (!ref) return { top: '50%', left: '50%' };
              const rect = ref.getBoundingClientRect();
              const bubbleWidth = window.innerWidth >= 768 ? 300 : 280;
              if (guideStep === 11 || guideStep === 12) {
                return {
                  top: rect.top + rect.height / 2,
                  left: rect.left - bubbleWidth - 32,
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              return {
                top: rect.top + rect.height / 2,
                left: rect.right + 32,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            })()}
          >
            <div className="pointer-events-auto">
            {/* Arrow pointing left (default) or right (step 11) */}
            {guideStep === 11 || guideStep === 12 ? (
              <>
                <div
                  className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderLeft: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            ) : (
              <>
                <div
                  className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderRight: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderRight: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            )}
            <div
              className="rounded-2xl p-5 text-center"
              style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
            >
              <div
                className="flex items-center justify-center gap-2 mb-2"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
                }}
              >
                <Icon name={[null, "Upload", "LayoutGrid", "ToggleRight", "Maximize2", "FolderOpen", "Home", "Video", "Cpu", "RectangleHorizontal", "SlidersHorizontal", "Sparkles", "Monitor"][guideStep] || "Settings"} size={18} className="text-white/50" />
                <span className="text-sm font-semibold text-white">Step {guideStep}</span>
              </div>
              <p
                className="text-sm text-white/80 leading-relaxed"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s',
                }}
              >
                {guideStep === 1 ? (
                  <>Upload any <span className="text-white font-medium italic">Moodboard Image / Concept Board</span>. Without a reference, the image generation will not start.</>
                ) : guideStep === 2 ? (
                  <>Upload any image which represents the <span className="text-white font-medium italic">position of furniture, room angle, and space management</span>, according to which the Concept Board will be organized. (Optional)</>
                ) : guideStep === 3 ? (
                  <>A toggle button which controls whether the <span className="text-white font-medium italic">interior style will be carried over</span> to the final generation with the Concept Board, or will be dismissed.</>
                ) : guideStep === 4 ? (
                  <>The <span className="text-white font-medium italic">room size</span> which you expect to receive in the final generation.</>
                ) : guideStep === 5 ? (
                  <>Generated images which you chose to store inside Roomwork.ai can be <span className="text-white font-medium italic">dragged and dropped</span> into 'Moodboard Image(s)', 'Room Layout Reference'.</>
                ) : guideStep === 6 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">type of room</span> you want to generate.</>
                ) : guideStep === 7 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">camera angle perspective</span> for your room generation.</>
                ) : guideStep === 8 ? (
                  <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                ) : guideStep === 9 ? (
                  <>Image ratio: vertical or horizontal (choose the precise ratio according to the dropdown menu icons). Different <span className="text-white font-medium italic">ratios</span> allow you to explore different room compositions.</>
                ) : guideStep === 10 ? (
                  <>A dropdown selector that allows you to choose the <span className="text-white font-medium italic">output image resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                ) : guideStep === 11 ? (
                  <>Hit <span className="text-white font-medium italic">'Generate Image'</span> once you're ready! Choose only the fields that are relevant to your needs.</>
                ) : guideStep === 12 ? (
                  <>Receive the generated image in the <span className="text-white font-medium italic">'Result'</span> window. Use the buttons in the bottom right corner of the Result window to <span className="text-white font-medium italic">download</span>, <span className="text-white font-medium italic">delete</span>, or <span className="text-white font-medium italic">regenerate</span> the image using the exact same settings.</>
                ) : null}
              </p>
              {guideStep <= 2 && (
                <div
                  className="mt-3 flex justify-center"
                  style={{
                    opacity: guideVisible ? 1 : 0,
                    transform: guideVisible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.95)',
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.25s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.25s',
                  }}
                >
                  <img
                    src={guideStep === 1 ? "/assets/images/guide-moodboard-example.jpg" : "/assets/images/guide-room-layout-example.png"}
                    alt={guideStep === 1 ? "Moodboard example" : "Room layout example"}
                    className="w-28 h-28 object-cover rounded-xl"
                    style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}
                  />
                </div>
              )}
              <button
                onClick={() => guideStep < 12 ? nextGuideStep() : closeGuide()}
                className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s',
                }}
              >
                {guideStep < 12 ? 'Next' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default MoodboardTo3DRoom;
